import { defineStore } from 'pinia';
import { request } from '@/api-connectors/useBasicRequests';
import { AddressType, INTEGRATION_API_PARTNER, SettingsType, ShopSettings } from '@/types/backend/settingsType';
import i18n, { useI18n } from '@/plugins/vue-i18n';
import { useProductLanguageStore } from '@/stores/languages';
import { Language, LanguageUnitMapping, Region } from '@/types/frontend/enums/languageEnums';

interface SettingsStore {
    settings: SettingsType;
    selectedShopDomain: string;
}

const getSettingsRoute = 'settings';
const setPayoutAddressRoute = 'settings/setPayoutAddress';
const setInvoiceAddressRoute = 'settings/setInvoiceAddress';
const setReturnAddressRoute = 'settings/setReturnAddress';
const setDefaultPaymentMethodRoute = 'settings/payment';
const setDefaultAffiliatePayoutMethodRoute = 'settings/affiliatePayout';
const retryPaymentRoute = 'settings/payment/retryPayment';
const setLocalizationRoute = 'settings/setLocalization/';
const setShopLocalizationRoute = 'settings/setShopLocalization';
const setNotificationRoute = 'settings/notification';
const setStripePaymentIDRoute = 'settings/payment/update_stripe_payment_method_id/';
const requestStripeSetupIntentRoute = 'settings/payment/requestStripeSetupIntent/';
const setOnboardingFlowCompletedRoute = 'settings/setOnboardingFlowCompleted/';
const saveMarketingInformationRoute = 'settings/saveMarketingInformation';
const setNewsletterAcceptedRoute = 'settings/setNewsletterAccepted/';
const generateApiTokenRoute = 'settings/apiKey';
const updateIntegrationApiInfosRoute = 'settings/integrationApiInfos';
const autoAvailabilityUpdateRoute = 'settings/autoAvailabilityUpdate';
const disconnectShopRoute = '/settings/setDisconnected';
const setHideDtfWarningRoute = 'settings/hideDtfWarning/';

export const useSettingsStore = defineStore('settings', {
    state: (): SettingsStore => {
        return {
            settings: {} as SettingsType,
            selectedShopDomain: '',
        };
    },
    actions: {
        async clearSettings() {
            this.settings.shopDomains = [];
            this.selectedShopDomain = '';
        },
        /**
         * Switch the active shop from code (e.g. a CTA that must open the settings of a
         * specific shop). Writes the same sessionStorage memory the shop selector uses,
         * so a reload does not fall back to shop #1.
         */
        selectShop(shopDomain: string) {
            if (!shopDomain) return;
            this.selectedShopDomain = shopDomain;
            sessionStorage.setItem('selectedShopDomain', shopDomain);
        },
        /**
         * @param preferredShopDomain shop a deep link is about (`?shopDomain=` in the URL).
         *        Used instead of the first shop when it belongs to this account, so
         *        links from our mails open the shop the order lives in.
         */
        async getSettings(preferredShopDomain?: string): Promise<boolean> {
            const response = await request<SettingsType>({
                method: 'get',
                route: getSettingsRoute,
            });
            if (!response) return false;
            this.initializeIntegrationApiInfos(response);
            this.settings = { ...response };

            //update language of browser
            if (!this.settings.preferredLanguage) {
                let language = i18n.locale as Language;
                if (!Object.values(Language).includes(language)) language = Language.EN;
                this.settings.preferredLanguage = language;
            }
            await this.setLocalizationDefaults(this.settings.preferredLanguage);
            const langStore = useProductLanguageStore();
            const { changeLanguage } = useI18n();
            langStore.setLocale(this.settings.preferredLanguage, this.settings.preferredUnitSystem, this.settings.preferredShoeSizeSystem);
            i18n.locale = this.settings.preferredLanguage;
            sessionStorage.setItem('locale', i18n.locale);
            changeLanguage(i18n.locale);
            if (this.settings.shopDomains.length > 0) {
                // getSettings() can legitimately be called more than once per page load (router
                // guard + individual page onMounted hooks aren't coordinated) -- always defaulting
                // to shopDomains[0] here made every later call silently undo whatever shop the
                // merchant had actually selected. Remembering the last selection in sessionStorage
                // and preferring it here (when still valid) makes repeated calls idempotent instead.
                // A deep link naming its shop wins over the memory - that is the whole point of it.
                const remembered = sessionStorage.getItem('selectedShopDomain');
                const wanted = [preferredShopDomain, remembered].find(domain => domain && this.settings.shopDomains.includes(domain));
                this.selectedShopDomain = wanted ?? this.settings.shopDomains[0];
                // Keep the memory in sync even when nothing was remembered yet (first visit this
                // session) -- otherwise a shop the merchant is already looking at, but never picked
                // from a dropdown, wouldn't actually be "remembered" for the next page/reload.
                sessionStorage.setItem('selectedShopDomain', this.selectedShopDomain);
            }
            return true;
        },
        initializeIntegrationApiInfos(response: SettingsType) {
            for (const key of Object.values(INTEGRATION_API_PARTNER)) {
                for (const shop of Object.values(response.shops)) {
                    if (!shop.integrationApiInfos[key]) {
                        shop.integrationApiInfos[key] = { webhookUrl: '' };
                    }
                }
            }
        },
        async setLocalizationDefaults(language: Language) {
            //set default values if not set
            if (!this.settings.preferredShoeSizeSystem || !this.settings.preferredUnitSystem) {
                const systems = LanguageUnitMapping[language];
                this.settings.preferredLanguage = language;
                this.settings.preferredShoeSizeSystem = this.settings.preferredShoeSizeSystem ?? systems.shoeSize;
                this.settings.preferredUnitSystem = this.settings.preferredUnitSystem ?? systems.unit;
                await this.setLocalization();
            }
            for (const shop of Object.values(this.settings.shops)) {
                await this.setShopLocalizationDefaults(shop);
            }
        },
        async setShopLocalizationDefaults(shop?: ShopSettings) {
            //set default values if not set
            if (!shop) shop = this.selectedShop;
            if (!shop) return;
            if (!shop.productLanguage || !shop.productUnitSystem || !shop.productShoeSizeSystem) {
                const language = shop.productLanguage ?? this.settings.preferredLanguage ?? Language.EN;
                const systems = LanguageUnitMapping[language];
                shop.productLanguage = language;
                shop.productUnitSystem = shop.productUnitSystem ?? this.settings.preferredUnitSystem ?? systems.unit;
                shop.productShoeSizeSystem = shop.productShoeSizeSystem ?? this.settings.preferredShoeSizeSystem ?? systems.shoeSize;
                await this.setShopLocalization(shop.shop_domain, { skipPrinterType: true });
            }
        },
        getUserFullName(): string | undefined {
            if (this.settings.name) return this.settings.name;
            if (this.selectedShop?.shop_address) return this.selectedShop.shop_address.split('<br>')[0].trim();
            if (this.selectedShop?.shop_owner) return this.selectedShop.shop_owner;
        },
        async setPayoutAddress(address: AddressType & { vatId?: string }): Promise<string | undefined> {
            const response = await request<string>({
                method: 'put',
                route: setPayoutAddressRoute,
                body: {
                    payoutAddress: {
                        shop_owner: address.shop_owner,
                        street1: address.address1,
                        street1_street: address.address1_street,
                        street1_number: address.address1_number,
                        street2: '',
                        city: address.city,
                        zip: address.zip,
                        country: address.country_name,
                        countryCode: address.countryCode,
                    },
                    vatId: address.vatId,
                },
            });
            return response;
        },
        async setInvoiceAddress(address: AddressType & { vatId?: string }): Promise<string | undefined> {
            const response = await request<string>({
                method: 'put',
                route: setInvoiceAddressRoute,
                body: {
                    invoiceAddress: {
                        shop_owner: address.shop_owner,
                        street1: address.address1,
                        street1_street: address.address1_street,
                        street1_number: address.address1_number,
                        street2: '',
                        city: address.city,
                        zip: address.zip,
                        country: address.country_name,
                        countryCode: address.countryCode,
                    },
                    vatId: address.vatId,
                },
            });
            return response;
        },
        async setReturnAddress(region: Region, address: AddressType): Promise<string | undefined> {
            if (!this.selectedShopDomain) return;
            const response = await request<string>({
                method: 'put',
                route: setReturnAddressRoute,
                body: {
                    region,
                    returnAddress: {
                        name: address.name,
                        shop_owner: address.shop_owner,
                        street1: address.address1,
                        street1_street: address.address1_street,
                        street1_number: address.address1_number,
                        street2: '',
                        city: address.city,
                        zip: address.zip,
                        country: address.country_name,
                        ...(address.province_code ? { province_code: address.province_code } : {}),
                    },
                },
            });
            return response;
        },
        async setDefaultPaymentMethod(region: Region, method: string | undefined) {
            if (region && method) {
                const response = await request<ShopSettings>({
                    method: 'post',
                    route: setDefaultPaymentMethodRoute,
                    body: {
                        region,
                        method,
                    },
                });
                if (!response) return;
                this.settings.shops[this.selectedShopDomain] = response;
            }
        },
        async deletePaymentMethod(region: Region, provider: 'stripe' | 'paypal'): Promise<boolean> {
            const response = await request<ShopSettings>({
                method: 'post',
                route: 'settings/payment/deleteMethod',
                body: { region, provider },
            });
            if (!response) return false;
            await this.getSettings();
            return true;
        },
        async setDefaultAffiliatePayoutMethod(method: string | undefined) {
            if (method) {
                const response = await request<SettingsType>({
                    method: 'post',
                    route: setDefaultAffiliatePayoutMethodRoute,
                    body: {
                        default: method,
                    },
                });
                if (!response) return;
                this.settings = response;
            }
        },
        async requestStripeSetupIntent(id: string, region: Region): Promise<{ confirmed: boolean; clientSecret?: string } | undefined> {
            return await request<{ confirmed: boolean; clientSecret?: string }>({
                method: 'post',
                route: `${requestStripeSetupIntentRoute}${region}`,
                body: {
                    payment_method_id: id,
                },
            });
        },
        async setStripePaymentID(id: string, region: Region): Promise<any> {
            return await request<any>({
                method: 'post',
                route: `${setStripePaymentIDRoute}${region}`,
                body: {
                    payment_method_id: id,
                },
            });
        },
        async retryPayment(orderId: string | null = null): Promise<boolean> {
            const response = await request<string>({
                method: 'get',
                route: retryPaymentRoute,
                body: {
                    ...(orderId ? { orderId: orderId } : null),
                },
            });
            return response === 'OK';
        },
        async setLocalization() {
            await request<void>({
                method: 'patch',
                route: setLocalizationRoute,
                body: {
                    preferredLanguage: this.settings.preferredLanguage,
                    preferredUnitSystem: this.settings.preferredUnitSystem,
                    preferredShoeSizeSystem: this.settings.preferredShoeSizeSystem,
                    primarySalesRegion: this.settings.primarySalesRegion,
                },
            });
            const langStore = useProductLanguageStore();
            langStore.setLocale(this.settings.preferredLanguage, this.settings.preferredUnitSystem, this.settings.preferredShoeSizeSystem);
        },
        async setShopLocalization(shopDomain?: string, { skipPrinterType = false }: { skipPrinterType?: boolean } = {}) {
            if (!shopDomain) shopDomain = this.selectedShopDomain;
            if (!shopDomain) return;
            const shop = this.settings.shops[shopDomain];
            await request<void>({
                method: 'patch',
                route: setShopLocalizationRoute,
                body: {
                    shopDomain: shop.shop_domain,
                    productLanguage: shop.productLanguage,
                    productUnitSystem: shop.productUnitSystem,
                    productShoeSizeSystem: shop.productShoeSizeSystem,
                    ...(!skipPrinterType && { preferredPrinterTypeTeeInBlue: shop.preferredPrinterTypeTeeInBlue }),
                },
            });
        },
        async setNotificationSettings(failedPayment: boolean, personalizedDesign: boolean) {
            const response = await request<ShopSettings>({
                method: 'post',
                route: setNotificationRoute,
                body: {
                    failed_payment: failedPayment,
                    personalized_design: personalizedDesign,
                },
            });
            if (!response) return;
            this.settings.shops[this.selectedShopDomain] = response;
        },
        async setOnboardingFlowCompleted(value: boolean, heardFrom?: string, heardFromAdditionalInfo?: string) {
            const shop = this.selectedShop;
            if (!shop) return;
            const body: { heardFrom?: string; heardFromAdditionalInfo?: string } = {};
            if (heardFrom) body.heardFrom = heardFrom;
            if (heardFromAdditionalInfo) body.heardFromAdditionalInfo = heardFromAdditionalInfo;
            return request<SettingsType>({
                method: 'patch',
                route: setOnboardingFlowCompletedRoute + value.toString(),
                body,
            }).then(() => {
                shop.onboardingFlowCompleted = value;
            });
        },
        async saveMarketingInformation(heardFrom?: string, heardFromAdditionalInfo?: string) {
            const body: { heardFrom?: string; heardFromAdditionalInfo?: string } = {};
            if (heardFrom) body.heardFrom = heardFrom;
            if (heardFromAdditionalInfo) body.heardFromAdditionalInfo = heardFromAdditionalInfo;
            return request<SettingsType>({
                method: 'post',
                route: saveMarketingInformationRoute,
                body,
            }).then(response => {
                if (response) {
                    this.settings.marketingInformation = response.marketingInformation;
                }
            });
        },
        async setNewsletterAccepted(value: boolean) {
            return request<SettingsType>({
                method: 'patch',
                route: setNewsletterAcceptedRoute + value.toString(),
            }).then(() => {
                this.settings = {
                    ...this.settings,
                    newsletterAccepted: value,
                };
            });
        },
        async setAutoAvailabilityUpdate() {
            const shop = this.selectedShop;
            if (!shop) return;
            return request<SettingsType>({
                method: 'patch',
                route: autoAvailabilityUpdateRoute + '/' + shop.autoAvailabilityUpdate,
            }).then(() => {});
        },
        async generateApiToken() {
            const shop = this.selectedShop;
            if (!shop) return;
            return request<string>({
                method: 'post',
                route: generateApiTokenRoute,
            }).then(response => {
                shop.apiToken = response;
            });
        },
        async updateIntegrationApiInfos() {
            const shop = this.selectedShop;
            if (!shop) return;
            return request<string>({
                method: 'patch',
                route: updateIntegrationApiInfosRoute,
                body: shop.integrationApiInfos,
            });
        },
        async toggleShopDisconnect() {
            const shop = this.selectedShop;
            if (!shop) return;
            const result = await request<Date | undefined>({
                method: 'patch',
                route: disconnectShopRoute + '/' + (shop.disconnectedAt ? false : true),
            });
            shop.disconnectedAt = result;
        },
        async setHideDtfWarning(value: boolean) {
            const result = await request<SettingsType>({
                method: 'patch',
                route: setHideDtfWarningRoute + value.toString(),
            });
            if (result) this.settings.hideDtfWarning = value;
        },
        async updateReturnManagement(shopDomain: string, isActive: boolean) {
            await request<{ confirmationUrl?: string }>({
                method: 'PATCH',
                route: `/subscriptions/return-management`,
                body: {
                    shopDomain,
                    action: isActive ? 'activate' : 'deactivate',
                    returnUrl: window.location.origin,
                },
            })
                .then(data => {
                    if (data?.confirmationUrl) {
                        window.open(data.confirmationUrl, '_blank');
                    }
                })
                .catch(err => {
                    console.log(err);
                });
            await this.getSettings();
            // return;
        },
    },
    getters: {
        marketingInformation: state => state.settings.marketingInformation,
        agbAccepted: state => {
            return state.settings.agbAccepted;
        },
        isSettingsLoaded: state => {
            return !!state.settings.email;
        },
        connectedShopDomains: state => {
            return state.settings.shopDomains
                .filter(shopDomain => state.settings.shops[shopDomain]?.isInstalled)
                .map(s => ({ value: s, text: s.replace('.myshopify.com', '') }));
        },
        onboardedShopDomains: state => {
            return state.settings.shopDomains
                .filter(shopDomain => state.settings.shops[shopDomain]?.onboardingFlowCompleted)
                .map(s => ({ value: s, text: s.replace('.myshopify.com', '') }));
        },
        availableShopDomains: state => {
            return state.settings.shopDomains
                .filter(shopDomain => {
                    const shop = state.settings.shops[shopDomain] ?? undefined;
                    return shop?.isInstalled && shop?.onboardingFlowCompleted;
                })
                .map(s => ({ value: s, text: s.replace('.myshopify.com', '') }));
        },
        selectedShop: (state): ShopSettings | undefined => {
            return state.settings.shops[state.selectedShopDomain ?? ''] ?? undefined;
        },
        isBetaTester: state => {
            return state.settings.isBetaTester ?? false;
        },
    },
});
